import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import {
  TelemetryPeriod,
  periodStart,
  samePeriod,
  telemetryPeriod,
} from '@core/constants/telemetry-route.constant';
import { ChartRangeOptions } from '@core/models';
import { TimeControlsComponent } from '@custom-components/general/time/time-controls/time-controls.component';
import { InstallationTelemetryStore } from '@root/app/store/installation-telemetry.store';
import moment from 'moment';
import type { Moment } from 'moment';

@Component({
  selector: 'xemex-installation-insights-configuration',
  imports: [TimeControlsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-insights-configuration.component.html',
})
export class InstallationInsightsConfigurationComponent {
  readonly chartRangeOptions: ChartRangeOptions[] = [
    ChartRangeOptions.DayPerQuarter,
    ChartRangeOptions.Day,
    ChartRangeOptions.Week,
    ChartRangeOptions.Month,
    ChartRangeOptions.Year,
  ];

  readonly telemetryStore = inject(InstallationTelemetryStore);

  // The store outlives this tab, so a period chosen on an earlier visit is still the one on
  // screen when the tab opens again. The control starts there rather than on today.
  readonly initialDate = this.keptPeriodStart();

  constructor() {
    // The page fires the first request when the tab opens; this effect owns every later one, so
    // opening the tab costs one request and each range or date change one more.
    let firstEffect = true;
    effect(() => {
      this.telemetryStore.selectedPeriod();
      if (!firstEffect) {
        this.telemetryStore.fetch();
      }
      firstEffect = false;
    });
  }

  private keptPeriodStart(): Moment | null {
    const selected = this.telemetryStore.selectedPeriod();
    const current = telemetryPeriod(this.telemetryStore.range(), moment());
    return samePeriod(selected, current) ? null : periodStart(selected);
  }

  onChartRangeChanged(range: ChartRangeOptions): void {
    this.telemetryStore.setRange(range);
  }

  onPeriodChanged(period: TelemetryPeriod): void {
    this.telemetryStore.setPeriod(period);
  }
}
